import { Box, Typography } from "@mui/material";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import BrushIcon from "../../../../assets/Labs/FiboEdit/MaskEditor/icons/BrushIcon";
import EraserIcon from "../../../../assets/VideoEditing/icons/EraserIcon";
import DeleteIcon from "../../../../assets/images/icons/DeleteIcon";
import { UploadIcon } from "../../../../assets/images/icons/UploadIcon";
import BriaButton from "../../../../components/common/BriaButton/BriaButton";
import BriaIconButton from "../../../../components/common/BriaIconButton/BriaIconButton";
import BriaSlider from "../../../../components/common/BriaSlider/BriaSlider";
import ConfigSwitch from "../../../../components/common/BriaSwitch/Config/ConfigSwitch";
import FileUploader from "../../../../components/common/FileUploader/FileUploader";
import { useAppStore } from "../../../../hooks/useStores";
import { showErrorToast } from "../../../../utils/toast";
import useImageUtils from "../../../../utils/useImageUtils";
import styles from "./ControlPanel.module.scss";

export type MaskType = "brush" | "eraser";

export interface BrushProps {
	size?: number;
	type?: MaskType;
}

interface MaskTypeChooserProps {
	maskType: MaskType;
	setMaskType: (type: MaskType) => void;
	disabled?: boolean;
}

const MaskTypeChooser = ({ maskType, setMaskType, disabled = false }: MaskTypeChooserProps) => {
	return (
		<Box className={styles.maskTypeChooser}>
			<BriaIconButton
				className={clsx(styles.maskTypeButton, { [styles.selected]: maskType === "brush" })}
				title="Brush"
				onClick={() => setMaskType("brush")}
				disabled={disabled}
			>
				<BrushIcon />
			</BriaIconButton>
			<BriaIconButton
				className={clsx(styles.maskTypeButton, { [styles.selected]: maskType === "eraser" })}
				title="Eraser"
				onClick={() => setMaskType("eraser")}
				disabled={disabled}
			>
				<EraserIcon />
			</BriaIconButton>
		</Box>
	);
};

interface BrushSizeSliderProps {
	brushSize: number;
	setBrushSize: (size: number) => void;
	disabled?: boolean;
}

const BRUSH_SIZE_MARKS = [1, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((value) => ({ value }));

const BrushSizeSlider = ({ brushSize, setBrushSize, disabled = false }: BrushSizeSliderProps) => {
	const { t } = useTranslation("fibo_edit", { keyPrefix: "lab.maskEditor.controlPanel.brushSizeSlider" });

	return (
		<Box className={styles.sliderContainer}>
			<BriaSlider
				value={brushSize}
				onChange={(_e, value) => setBrushSize(value as number)}
				step={null}
				marks={BRUSH_SIZE_MARKS}
				valueLabelDisplay="auto"
				valueLabelFormat={(value) => `${value}${t("brushSizeScaleUnit")}`}
				disabled={disabled}
			/>
			<Typography className={clsx(styles.brushSizeLabel, { [styles.disabled]: disabled })}>
				{brushSize}
				{t("brushSizeScaleUnit")}
			</Typography>
		</Box>
	);
};

interface MaskEditorControlsRowProps {
	brush: {
		size: number;
		type: MaskType;
		onChange: (size: number, type: MaskType) => void;
	};
	onUseMask?: (maskFileBase64: string) => Promise<void>;
	onResetMask: () => void;
	maskEditorEnabled?: boolean;
	onMaskEditorToggle?: (enabled: boolean) => void;
	showToggle?: boolean;
	className?: string;
	isImageLoading?: boolean;
}

const MaskEditorControlsRow = ({
	brush,
	onUseMask,
	onResetMask,
	maskEditorEnabled,
	onMaskEditorToggle,
	showToggle,
	className,
	isImageLoading = false,
}: MaskEditorControlsRowProps) => {
	const { t } = useTranslation("fibo_edit", { keyPrefix: "lab.maskEditor.controlPanel" });
	const { fiboStore } = useAppStore();
	const imageUtils = useImageUtils();
	const maskEditorStore = fiboStore.fiboEditStore.maskEditorStore;

	const handleMaskTypeChange = (type: MaskType) => {
		if (!maskEditorEnabled) return;
		brush.onChange(brush.size, type);
	};

	const handleBrushSizeChange = (size: number) => {
		if (!maskEditorEnabled) return;
		brush.onChange(size, brush.type);
	};

	const handleResetMask = () => {
		if (!maskEditorEnabled) return;
		onResetMask?.();
	};

	const handleMaskUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!maskEditorEnabled) return;
		try {
			const file = e.target.files?.[0];
			if (!file) throw new Error("No file selected");

			const base64 = await imageUtils.getBase64(file);
			if (!base64) throw new Error("Failed to convert file to base64");

			const isValidAspectRatio = await maskEditorStore.validateMaskAspectRatio(base64 as string);
			if (!isValidAspectRatio) {
				throw new Error("Uploaded mask must have the same aspect ratio as the original image");
			}

			await maskEditorStore.loadMaskOntoCanvas(base64 as string);
		} catch (ex) {
			console.error(ex);
			showErrorToast((ex as Error).message);
		}
	};

	const handleUseMask = async () => {
		const maskFileBase64 = await maskEditorStore.getMaskFileBase64();
		await onUseMask?.(maskFileBase64);
		maskEditorStore.isEditorOpened = false;
	};

	return (
		<Box className={clsx(styles.controlsRow, className)}>
			{showToggle && (
				<>
					<Box className={styles.toggleSection}>
						<ConfigSwitch
							label={t("createMask.label")}
							info={t("createMask.tooltip")}
							checked={!!maskEditorEnabled}
							onChange={onMaskEditorToggle ?? (() => {})}
							className={styles.maskEditorToggle}
							disabled={isImageLoading}
						/>
					</Box>
				</>
			)}

			<Box className={styles.brushSection}>
				<MaskTypeChooser
					maskType={brush.type}
					setMaskType={handleMaskTypeChange}
					disabled={!maskEditorEnabled}
				/>
				<BrushSizeSlider
					brushSize={brush.size}
					setBrushSize={handleBrushSizeChange}
					disabled={!maskEditorEnabled}
				/>
			</Box>
			<Box className={styles.controlsCenter}>
				<FileUploader
					buttonText={t("uploadMask")}
					buttonType="textSmall"
					buttonClassName={styles.controlButton}
					maxFilesLimit={1}
					inputProps={{ accept: "image/png, image/jpg, image/jpeg, image/webp", multiple: false }}
					buttonProps={{ startIcon: <UploadIcon /> }}
					onUpload={handleMaskUpload}
					disabled={!maskEditorEnabled}
				/>
				<BriaButton
					className={styles.deleteButton}
					buttonType="textSmall"
					onClick={() => handleResetMask()}
					disabled={!maskEditorEnabled}
					startIcon={<DeleteIcon />}
				>
					{t("discardMask")}
				</BriaButton>
			</Box>
			<Box className={clsx(styles.controlsRight, { [styles.hide]: true })}>
				<BriaButton
					className={styles.useMaskButton}
					buttonType="primary"
					onClick={() => handleUseMask()}
					disabled={!maskEditorEnabled}
				>
					{t("useMaskButton")}
				</BriaButton>
			</Box>
		</Box>
	);
};

export default MaskEditorControlsRow;
